import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { History, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ContentGeneratorProps {
  onInspirationSelect: (text: string) => void;
  onClose: () => void;
}

export const ContentGenerator = ({ onInspirationSelect, onClose }: ContentGeneratorProps) => {
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [promptHistory, setPromptHistory] = useState<{ id: string, prompt: string, created_at: string }[]>([]);

  useEffect(() => {
    fetchPromptHistory();
  }, []);

  const fetchPromptHistory = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('content_generation_prompts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(5);

    if (error) {
      console.error("Error fetching prompt history:", error);
      return;
    }

    setPromptHistory(data || []);
  };

  const handleGenerateContent = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a topic or idea");
      return;
    }

    setIsLoading(true);
    // Close the modal smoothly
    onClose();
    // Show generating toast and store its ID
    const loadingToastId = toast.loading("Generating content...");

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Save prompt to history
      await supabase.from('content_generation_prompts').insert({
        user_id: user.id,
        prompt: prompt.trim()
      });

      const { data, error } = await supabase.functions.invoke('generate-inspiration', {
        body: { prompt },
      });

      if (error) throw error;
      
      onInspirationSelect(data.generatedText);
      setPrompt("");
      // Dismiss the loading toast before showing success
      toast.dismiss(loadingToastId);
      toast.success("Content generated successfully!");

      // Refresh prompt history after successful generation
      await fetchPromptHistory();
    } catch (error) {
      console.error("Error generating content:", error);
      // Dismiss the loading toast before showing error
      toast.dismiss(loadingToastId);
      toast.error("Failed to generate content");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Textarea
        placeholder="Enter a topic or idea (e.g., AI in healthcare)"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        className="min-h-[120px] resize-none"
      />
      <Button 
        className="w-full" 
        onClick={handleGenerateContent} 
        disabled={isLoading}
      >
        {isLoading ? "Generating..." : "Generate Content"}
      </Button>

      <div className="pt-4 border-t">
        <div className="flex items-center gap-2 mb-2">
          <History className="w-4 h-4" />
          <h3 className="text-sm font-semibold">Prompt History</h3>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <Info 
                  className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground transition-colors" 
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>Click on a prompt below to bring it back into the prompt window.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="space-y-2">
          {promptHistory.length > 0 ? (
            promptHistory.map((item) => (
              <button
                key={item.id}
                onClick={() => setPrompt(item.prompt)}
                className="w-full text-left px-3 py-2 text-sm rounded-md hover:bg-muted transition-colors duration-200"
              >
                {item.prompt}
              </button>
            ))
          ) : (
            <p className="text-sm text-muted-foreground px-3 py-2">Your prompts will be saved here.</p>
          )}
        </div>
      </div>
    </div>
  );
};